import { useCallback, useEffect, useState } from "react";
import type { AnchorHTMLAttributes, ComponentType, ReactNode } from "react";
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "../../lib/cn";
import { Badge } from "../Badge";
import { Tooltip } from "../Tooltip";

export type NavIcon = ComponentType<{ className?: string }>;

export interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: NavIcon;
  /** Contador (ex.: incidentes CRITICAL). `0` não é exibido. */
  badge?: number | string;
  /** Badge sólido chama mais atenção — reserve para CRITICAL. */
  badgeTone?: "neutral" | "danger" | "warning";
  disabled?: boolean;
}

export interface NavGroup {
  id: string;
  /** Sem label = grupo sem cabeçalho (itens de topo). */
  label?: string;
  items: NavItem[];
  /** Grupo com cabeçalho pode colapsar. Padrão: true quando há label. */
  collapsible?: boolean;
  defaultOpen?: boolean;
}

export interface SidebarRenderLinkArgs {
  item: NavItem;
  active: boolean;
  props: AnchorHTMLAttributes<HTMLAnchorElement>;
  children: ReactNode;
}

export interface SidebarProps {
  groups: NavGroup[];
  /** `id` do item ativo. Quem resolve a rota é o app, não a UI. */
  activeId?: string;
  /** Marca no topo (logo + nome). Altura fixa de 56px. */
  brand?: ReactNode;
  /** Rodapé fixo: status agregado dos monitores + conta (seção 6). */
  footer?: ReactNode;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Esconde o botão de colapsar (usado na versão drawer do mobile). */
  showCollapseToggle?: boolean;
  /** Chamado ao clicar num item — o AppShell usa para fechar o drawer. */
  onNavigate?: (item: NavItem) => void;
  /**
   * Integração com router (Link do TanStack/React Router). Sem isso,
   * o item vira `<a href>` puro.
   */
  renderLink?: (args: SidebarRenderLinkArgs) => ReactNode;
  className?: string;
}

const COLLAPSED_KEY = "mgdlead.sidebar.collapsed";

/** Persistência do colapso — localStorage pode lançar (aba privada). */
export function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    /* sem persistência: vale só nesta sessão */
  }
}

/**
 * Navegação lateral: painel branco de raio 16, 272px expandida / 72px colapsada.
 * Item ativo = preenchido em TINTA (`surface-inverse`, escuro no tema claro) —
 * nunca em violeta: a cor da marca fica reservada para o único CTA da tela.
 * Rótulo de grupo em mono caixa-alta; rodapé num bloco cinza (status dos monitores).
 */
export function Sidebar({
  groups,
  activeId,
  brand,
  footer,
  collapsed: controlledCollapsed,
  onCollapsedChange,
  showCollapseToggle = true,
  onNavigate,
  renderLink,
  className,
}: SidebarProps) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const collapsed = controlledCollapsed ?? uncontrolled;

  useEffect(() => {
    if (controlledCollapsed === undefined) setUncontrolled(readSidebarCollapsed());
  }, [controlledCollapsed]);

  const toggleCollapsed = useCallback(() => {
    const next = !collapsed;
    writeSidebarCollapsed(next);
    if (controlledCollapsed === undefined) setUncontrolled(next);
    onCollapsedChange?.(next);
  }, [collapsed, controlledCollapsed, onCollapsedChange]);

  return (
    <div
      data-collapsed={collapsed || undefined}
      className={cn(
        "flex h-full flex-col rounded-lg bg-surface px-3 pb-3 pt-5",
        collapsed ? "w-sidebar-collapsed" : "w-sidebar",
        className,
      )}
    >
      {brand ? (
        <div
          className={cn(
            "flex shrink-0 items-center gap-2 pb-5",
            collapsed ? "justify-center" : "px-2",
          )}
        >
          {brand}
        </div>
      ) : null}

      <nav
        aria-label="Navegação principal"
        className="-mx-1 flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-1 scrollbar-thin"
      >
        {groups.map((group) => (
          <SidebarGroup
            key={group.id}
            group={group}
            collapsed={collapsed}
            {...(activeId !== undefined ? { activeId } : {})}
            {...(onNavigate ? { onNavigate } : {})}
            {...(renderLink ? { renderLink } : {})}
          />
        ))}
      </nav>

      {footer ? (
        <div
          className={cn(
            "mt-3 flex shrink-0 flex-col gap-1.5 rounded-md bg-surface-subtle",
            collapsed ? "p-2" : "p-3",
          )}
        >
          {footer}
        </div>
      ) : null}

      {showCollapseToggle ? (
        <div className={cn("mt-2 shrink-0", collapsed && "flex justify-center")}>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expandir menu lateral" : "Recolher menu lateral"}
            aria-expanded={!collapsed}
            className={cn(
              "inline-flex h-8 items-center gap-2 rounded-pill px-3 text-xs text-muted-foreground",
              "hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              collapsed ? "w-8 justify-center px-0" : "w-full",
            )}
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden="true" className="size-4 shrink-0" />
            ) : (
              <>
                <PanelLeftClose aria-hidden="true" className="size-4 shrink-0" />
                <span>Recolher</span>
              </>
            )}
          </button>
        </div>
      ) : null}
    </div>
  );
}

interface SidebarGroupProps {
  group: NavGroup;
  collapsed: boolean;
  activeId?: string;
  onNavigate?: (item: NavItem) => void;
  renderLink?: (args: SidebarRenderLinkArgs) => ReactNode;
}

function SidebarGroup({ group, collapsed, activeId, onNavigate, renderLink }: SidebarGroupProps) {
  const collapsible = group.collapsible ?? Boolean(group.label);
  const [open, setOpen] = useState(group.defaultOpen ?? true);
  const showItems = !collapsible || open || collapsed;

  return (
    <div>
      {group.label && !collapsed ? (
        collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            className="flex w-full items-center justify-between gap-2 rounded-sm px-3 pb-1.5 font-mono text-2xs font-medium uppercase tracking-caps text-subtle-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span>{group.label}</span>
            <ChevronDown
              aria-hidden="true"
              className={cn(
                "size-3 shrink-0 transition-transform duration-[var(--duration-micro)]",
                !open && "-rotate-90",
              )}
            />
          </button>
        ) : (
          <p className="px-3 pb-1.5 font-mono text-2xs font-medium uppercase tracking-caps text-subtle-foreground">
            {group.label}
          </p>
        )
      ) : null}

      {/* separador discreto no modo colapsado, já que o título some */}
      {group.label && collapsed ? <div className="mx-2 mb-2 h-px bg-border" /> : null}

      {showItems ? (
        <ul className="flex flex-col gap-0.5">
          {group.items.map((item) => (
            <li key={item.id}>
              <SidebarLink
                item={item}
                collapsed={collapsed}
                active={item.id === activeId}
                {...(onNavigate ? { onNavigate } : {})}
                {...(renderLink ? { renderLink } : {})}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface SidebarLinkProps {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  onNavigate?: (item: NavItem) => void;
  renderLink?: (args: SidebarRenderLinkArgs) => ReactNode;
}

function SidebarLink({ item, collapsed, active, onNavigate, renderLink }: SidebarLinkProps) {
  const Icon = item.icon;
  const showBadge = item.badge !== undefined && item.badge !== 0 && item.badge !== "0";

  const anchorProps: AnchorHTMLAttributes<HTMLAnchorElement> = {
    href: item.disabled ? undefined : item.href,
    "aria-current": active ? "page" : undefined,
    "aria-disabled": item.disabled || undefined,
    onClick: () => onNavigate?.(item),
    className: cn(
      "group/nav relative flex h-9 items-center gap-2.5 rounded-md px-3 text-sm font-medium",
      "transition-colors duration-[var(--duration-micro)]",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
      // alvo de toque de 44px no mobile (a sidebar vira drawer lá)
      "max-md:min-h-11",
      collapsed && "mx-auto size-10 justify-center px-0",
      active
        ? "bg-surface-inverse text-inverse-foreground"
        : "text-foreground hover:bg-muted",
      item.disabled && "pointer-events-none opacity-50",
    ),
  };

  const content = (
    <>
      <Icon
        aria-hidden="true"
        className={cn("size-4 shrink-0", active ? "text-inverse-foreground" : "text-muted-foreground")}
      />
      {collapsed ? (
        <span className="sr-only">{item.label}</span>
      ) : (
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
      )}
      {showBadge && !collapsed ? (
        <Badge
          tone={item.badgeTone ?? "neutral"}
          solid={item.badgeTone === "danger"}
          numeric
          className="shrink-0"
        >
          {item.badge}
        </Badge>
      ) : null}
      {showBadge && collapsed ? (
        <span
          aria-hidden="true"
          className={cn(
            "absolute right-1.5 top-1.5 size-1.5 rounded-full ring-2 ring-surface",
            item.badgeTone === "danger" ? "bg-danger" : item.badgeTone === "warning" ? "bg-warning" : "bg-border-strong",
          )}
        />
      ) : null}
    </>
  );

  const link = renderLink ? (
    renderLink({ item, active, props: anchorProps, children: content })
  ) : (
    <a {...anchorProps}>{content}</a>
  );

  // colapsada, o ícone é a única informação: tooltip + sr-only cobrem os dois
  return collapsed ? (
    <Tooltip content={item.label} side="right">
      <span className="block">{link}</span>
    </Tooltip>
  ) : (
    <>{link}</>
  );
}
